import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import TalentaCTA from "@/components/TalentaCTA";
import { GraduationCap, Milestone, ShieldCheck, ExternalLink, CheckCircle2, ListChecks, Lightbulb, School } from "lucide-react";

const JALUR_ICON = { akademik: GraduationCap, sipil: Milestone, taruna: ShieldCheck };
const JALUR_COLOR = { akademik: "#E25E3E", sipil: "#4A6B53", taruna: "#1A1C1A" };
const ORDER = ["akademik", "sipil", "taruna"];

function Bullets({ title, items, icon: Ic, color }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#1A1C1A]"><Ic className="h-4 w-4" style={{ color }} /> {title}</div>
      <ul className="space-y-1.5">
        {items.map((t, i) => (
          <li key={i} className="text-sm text-[#5C5F5C] flex gap-2"><span style={{ color }}>•</span><span>{t}</span></li>
        ))}
      </ul>
    </div>
  );
}

export default function InfoJalur() {
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const fokus = params.get("fokus");
  const [active, setActive] = useState(fokus && ORDER.includes(fokus) ? fokus : "akademik");

  useEffect(() => {
    api.get("/jalur/info").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) {
    return <AppShell><p className="text-[#5C5F5C]" data-testid="infojalur-loading">Memuat info jalur…</p></AppShell>;
  }

  const meta = data.meta || {};
  const info = data.jalur[active];
  const color = JALUR_COLOR[active];
  const Icon = JALUR_ICON[active];

  const wilayah = [...new Set(data.sekolah_unggul.map((s) => s.wilayah))];

  return (
    <AppShell>
      <div className="mb-8">
        <p className="text-[#5C5F5C]">Panduan Pendidikan Lanjutan</p>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-[#1A1C1A]">Info Jalur & Link Resmi</h1>
        <p className="text-[#5C5F5C] mt-2 max-w-2xl">Kenali syarat, tahapan seleksi, dan tautan pendaftaran resmi setiap jalur. Selalu cek situs resmi untuk jadwal & ketentuan terbaru tiap tahun.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="jalur-tabs">
        {ORDER.map((kode) => {
          const Ic = JALUR_ICON[kode];
          const on = active === kode;
          return (
            <button key={kode} onClick={() => setActive(kode)} data-testid={`jalur-tab-${kode}`}
              className={`px-4 py-2.5 rounded-full text-sm font-medium flex items-center gap-2 transition-colors border-2 ${on ? "text-white border-transparent" : "text-[#1A1C1A] border-[#1A1C1A]/15 hover:bg-[#1A1C1A]/5"}`}
              style={on ? { background: JALUR_COLOR[kode] } : {}}>
              <Ic className="h-4 w-4" /> {meta[kode]?.label || kode}
            </button>
          );
        })}
      </div>

      {/* Active jalur detail */}
      <div className="clay-card p-7 mb-4" data-testid={`jalur-detail-${active}`}>
        <div className="flex items-start gap-4 mb-5">
          <span className="h-12 w-12 rounded-xl grid place-items-center text-white shrink-0" style={{ background: color }}><Icon className="h-6 w-6" /></span>
          <div>
            <h2 className="font-heading font-black text-xl text-[#1A1C1A]">{meta[active]?.label}</h2>
            <p className="text-sm text-[#5C5F5C] mt-1">{info.ringkasan}</p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <Bullets title="Syarat Umum" items={info.syarat} icon={CheckCircle2} color={color} />
          <Bullets title="Tahapan Seleksi" items={info.tahapan} icon={ListChecks} color={color} />
          <Bullets title="Tips Persiapan" items={info.tips} icon={Lightbulb} color={color} />
        </div>
        <div className="mt-6 pt-5 border-t border-[#F0ECE4]">
          <p className="text-sm font-semibold text-[#1A1C1A] mb-3">Link Pendaftaran Resmi</p>
          <div className="flex flex-wrap gap-3">
            {info.link.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`jalur-link-${active}-${i}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium text-white transition-transform hover:-translate-y-0.5"
                style={{ background: color }}>
                {l.label} <ExternalLink className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Sekolah unggul */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-1">
          <School className="h-5 w-5 text-[#E25E3E]" />
          <h2 className="font-heading font-black text-2xl text-[#1A1C1A]">SMA Unggul & Berasrama</h2>
        </div>
        <p className="text-[#5C5F5C] mb-5">Referensi penerimaan siswa baru (PPDB/SPMB) sekolah menengah unggulan.</p>
        {wilayah.map((w) => (
          <div key={w} className="mb-6">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#5C5F5C] mb-3">{w}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.sekolah_unggul.filter((s) => s.wilayah === w).map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`sekolah-card-${w}-${i}`}
                  className="clay-card clay-card-hover p-5 block group">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-heading font-bold text-base text-[#1A1C1A] leading-snug">{s.nama}</h4>
                    <ExternalLink className="h-4 w-4 text-[#5C5F5C] group-hover:text-[#E25E3E] shrink-0" />
                  </div>
                  <p className="text-xs font-medium text-[#E25E3E] mb-1.5">{s.lokasi}</p>
                  <p className="text-sm text-[#5C5F5C]">{s.keterangan}</p>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
      <TalentaCTA className="mt-8" title="Siapkan diri untuk jalur pilihanmu" sub="Bimbingan SNBP/SNBT/Kedinasan, latihan & TryOut lengkap di Bimbel Talenta." />
    </AppShell>
  );
}
